import { loadStripe, Stripe } from '@stripe/stripe-js';
import { STRIPE_CONFIG, isStripeConfigured } from '../config/stripe';
import { logger } from '../utils/logger';

let stripePromise: Promise<Stripe | null> | null = null;

export const getStripe = () => {
  if (!stripePromise && isStripeConfigured()) {
    stripePromise = loadStripe(STRIPE_CONFIG.publishableKey);
  }
  return stripePromise;
};

export interface CheckoutOptions {
  priceId: string;
  userId?: string;
  email?: string;
  metadata?: Record<string, string>;
}

export const createCheckoutSession = async (options: CheckoutOptions) => {
  try {
    // In a full implementation, this would call your backend API
    // For now, we'll use Stripe Checkout directly (requires backend later)
    
    const stripe = await getStripe();
    if (!stripe) {
      throw new Error('Stripe is not configured');
    }

    // Call the Netlify function to create checkout session
    const response = await fetch('/.netlify/functions/create-checkout-session', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        priceId: options.priceId,
        userId: options.userId,
        email: options.email,
        metadata: options.metadata,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to create checkout session');
    }

    const { sessionId } = await response.json();
    
    // Redirect to Stripe Checkout
    const { error } = await stripe.redirectToCheckout({
      sessionId,
    });

    if (error) {
      throw error;
    }
  } catch (error) {
    logger.error('Stripe checkout error', error);
    throw error;
  }
};

// Smart demo/production mode switching
const isDemoMode = () => {
  // In production, only use demo mode if explicitly enabled AND Stripe isn't configured
  if (process.env.NODE_ENV === 'production') {
    // Production: Only demo mode if Stripe is not properly configured
    const hasValidConfig = isStripeConfigured();
    const demoModeForced = localStorage.getItem('stripe-demo-mode') === 'true';

    // Log configuration status for debugging
    if (!hasValidConfig) {
      logger.warn('Stripe not configured properly - using demo mode');
    }

    return !hasValidConfig || demoModeForced;
  }

  // Development: Use demo mode by default, but allow override
  return localStorage.getItem('stripe-demo-mode') !== 'false';
};

// Utility functions for demo mode management
export const clearDemoMode = () => {
  localStorage.removeItem('stripe-demo-mode');
  logger.debug('Demo mode cleared');
};

export const enableDemoMode = () => {
  localStorage.setItem('stripe-demo-mode', 'true');
  logger.debug('Demo mode enabled');
};

export const getStripeDebugInfo = () => {
  return {
    environment: process.env.NODE_ENV,
    isStripeConfigured: isStripeConfigured(),
    publishableKeyPrefix: STRIPE_CONFIG.publishableKey.substring(0, 8) + '...',
    demoModeLocalStorage: localStorage.getItem('stripe-demo-mode'),
    isDemoMode: isDemoMode()
  };
};

// For testing/demo purposes - with production fallback
export const createDirectCheckout = async (priceId: string, email?: string) => {
  // Debug info for production troubleshooting
  logger.debug('Stripe Debug Info', getStripeDebugInfo());

  // Demo mode: Show confirmation dialog
  if (isDemoMode()) {
    logger.debug('Demo Mode: Would create checkout', { priceId, email });
    
    const productInfo = getProductInfo(priceId.includes('yearly') ? 'yearly' : 'monthly');
    const shouldSimulateSuccess = window.confirm(
      `Demo Mode: This would redirect to Stripe Checkout for the ${priceId.includes('yearly') ? 'yearly' : 'monthly'} plan (${formatPrice(productInfo.price)}).\n\nClick OK to simulate successful payment, Cancel to simulate payment failure.`
    );
    
    if (shouldSimulateSuccess) {
      // Simulate successful payment
      logger.debug('Demo: Payment successful');
      return { success: true, demo: true };
    } else {
      // Simulate payment cancellation
      throw new Error('Demo: Payment cancelled by user');
    }
  }

  // Production mode: Use real Stripe API
  try {
    const response = await fetch('/.netlify/functions/create-checkout-session', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        priceId,
        email,
        userId: 'user_' + Date.now(), // In production, use real user ID
        metadata: {
          source: '54-day-novena-app',
          timestamp: new Date().toISOString()
        }
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.error || `HTTP error! status: ${response.status}`;
      logger.error('Checkout session creation failed', { status: response.status, error: errorData });
      throw new Error(errorMessage);
    }

    const { sessionId, url } = await response.json();
    
    // Redirect to Stripe Checkout
    if (url) {
      window.location.href = url;
    } else {
      const stripe = await getStripe();
      if (!stripe) {
        throw new Error('Stripe is not configured');
      }
      
      const { error } = await stripe.redirectToCheckout({ sessionId });
      if (error) {
        throw error;
      }
    }
    
    return { success: true, demo: false };
    
  } catch (error) {
    logger.error('Stripe checkout error', error);
    throw error;
  }
};

export const getProductInfo = (productType: 'monthly' | 'yearly') => {
  return STRIPE_CONFIG.products[productType];
};

export const formatPrice = (price: number, currency = 'usd') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(price);
};